import { useEffect, useRef, useCallback } from 'react'
import { apiGetJson } from '../lib/apiClient'
import { useToast } from '../lib/toast'
import { unwrapList } from '../lib/apiUnwrap'

type RecentPurchase = {
  customer_name: string
  customer_avatar_url?: string
  product_name: string
  purchase_time: string
  price: number
  product_image_url?: string
}

const RECENT_PURCHASE_FETCH_INTERVAL_MS = 5000
const RECENT_PURCHASE_TOAST_INTERVAL_MS = 5000
const RECENT_PURCHASE_TOAST_INITIAL_DELAY_MS = 5000
const RECENT_PURCHASE_FETCH_LIMIT = 30

function getRelativeTime(isoString: string) {
  const diff = Date.now() - new Date(isoString).getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) return `${days} ngày trước`
  if (hours > 0) return `${hours} giờ trước`
  if (minutes > 0) return `${minutes} phút trước`
  return 'Vừa xong'
}

export default function RecentPurchasesNotification() {
  const { toast } = useToast()
  const purchasesRef = useRef<RecentPurchase[]>([])
  const currentIndexRef = useRef(0)
  const timeoutRef = useRef<number | null>(null)

  const fetchPurchases = useCallback(async () => {
    try {
      const resp = await apiGetJson<any>(`/api/recent-purchases?limit=${RECENT_PURCHASE_FETCH_LIMIT}`)
      const data = unwrapList<RecentPurchase>(resp)
      if (data.length > 0) {
        purchasesRef.current = data
        if (currentIndexRef.current >= data.length) {
          currentIndexRef.current = 0
        }
      }
    } catch (err) {
      console.error('Failed to fetch recent purchases:', err)
    }
  }, [])

  const showNextToast = useCallback(() => {
    const purchases = purchasesRef.current
    if (purchases.length === 0) {
      timeoutRef.current = window.setTimeout(showNextToast, RECENT_PURCHASE_TOAST_INTERVAL_MS)
      return
    }

    const current = purchases[currentIndexRef.current]
    // customer_name is already masked by backend (e.g., "*****Thái")
    const customerName = current.customer_name
    const relativeTime = getRelativeTime(current.purchase_time)

    // Show toast at bottom-left with 5 second duration and progress bar
    toast({
      title: `${customerName} vừa mua`,
      message: `${current.product_name} • ${relativeTime}`,
      variant: 'info',
      durationMs: 5000,
      position: 'bottom-left',
      showProgress: true,
      saveToNotifications: false,
      avatarUrl: current.customer_avatar_url || undefined,
      productImageUrl: current.product_image_url || undefined,
      customerInitials: customerName,
    })

    // Move to next item
    currentIndexRef.current = (currentIndexRef.current + 1) % purchases.length

    timeoutRef.current = window.setTimeout(showNextToast, RECENT_PURCHASE_TOAST_INTERVAL_MS)
  }, [toast])

  useEffect(() => {
    // Initial fetch
    fetchPurchases()

    // Poll backend every 5 seconds for credibility notifications
    const interval = setInterval(fetchPurchases, RECENT_PURCHASE_FETCH_INTERVAL_MS)

    // Start showing toasts after initial delay
    timeoutRef.current = window.setTimeout(showNextToast, RECENT_PURCHASE_TOAST_INITIAL_DELAY_MS)

    return () => {
      clearInterval(interval)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [fetchPurchases, showNextToast])

  // This component doesn't render anything - it just manages the toast cycle
  return null
}
